import { useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Trash2, RotateCcw, Check } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { ask } from "@/lib/confirm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { BottomSheet, useIsMobile } from "@/components/shared/PagePrimitives";

// Nastavení → Workflow úkolů (231, převzato z ProfitPilotu). U každé fáze zakázky si
// firma určí, jaké úkoly vzniknou samy: název, typ, komu (stavbyvedoucí, kdo fázi
// změnil, pozice, konkrétní člověk), termín „za N dnů", stálý popis, priorita a jestli
// se úkol odškrtne sám, když zakázka fázi opustí. Mění majitel a asistentka.
//
// Úpravy se nejdřív projeví jen na obrazovce; do systému jdou až tlačítkem Uložit
// v liště dole. Zrušit vrátí stav z posledního uložení.

const PORADI = ["poptavka", "nabidka", "smlouva", "realizace", "dokonceno", "fakturovano", "zaruka"];
const PRAZDNA = (faze) => ({ faze, nazev: "", typ: "task", komu: "STAVBYVEDOUCI", pozice: null, user_id: null,
  termin_dni: 3, pracovni_dny: true, popis: "", priorita: "Střední", uzavreni: "RUCNE", aktivni: true });
const POLE = ["faze", "nazev", "typ", "komu", "pozice", "user_id", "termin_dni", "pracovni_dny", "popis", "priorita", "uzavreni", "aktivni"];
const telo = (s) => Object.fromEntries(POLE.map((k) => [k, k === "termin_dni" && s[k] === "" ? 0 : s[k]]));
const stejne = (a, b) => JSON.stringify(telo(a)) === JSON.stringify(telo(b));
let dalsiNove = 0;

function terminText(s) {
  if (!s.termin_dni) return "hned";
  const n = s.termin_dni;
  const dny = n === 1 ? "den" : n < 5 ? "dny" : "dní";
  return `za ${n} ${s.pracovni_dny ? "prac. " : ""}${dny}`;
}

export function WorkflowUkoly({ canEdit }) {
  const mobile = useIsMobile();
  const [data, setData] = useState(null);
  const [koncept, setKoncept] = useState([]);
  const [tym, setTym] = useState([]);
  const [edit, setEdit] = useState(null);
  const [ukladam, setUkladam] = useState(false);
  const [ulozeno, setUlozeno] = useState(false);
  const [chyba, setChyba] = useState("");

  const prijmout = (d) => { setData(d); setKoncept(d.sablony.map((s) => ({ ...s }))); };
  useEffect(() => {
    api.get("/workflow/tasks").then((r) => prijmout(r.data)).catch((e) => setChyba(formatApiError(e.response?.data?.detail)));
    api.get("/team").then((r) => setTym((r.data || []).filter((m) => m.status !== "inactive"))).catch(() => {});
  }, []);

  const skupiny = useMemo(() => (data ? PORADI.filter((k) => data.faze[k]).map((k) => ({
    klic: k, nazev: data.faze[k], sablony: koncept.filter((s) => s.faze === k) })) : []), [data, koncept]);

  const zmeny = useMemo(() => {
    if (!data) return { nove: [], upravene: [], smazane: [] };
    const puv = Object.fromEntries(data.sablony.map((s) => [s.id, s]));
    const ted = new Set(koncept.map((s) => s.id));
    return { nove: koncept.filter((s) => !puv[s.id]), upravene: koncept.filter((s) => puv[s.id] && !stejne(s, puv[s.id])),
      smazane: data.sablony.filter((s) => !ted.has(s.id)) };
  }, [data, koncept]);
  const pocetZmen = zmeny.nove.length + zmeny.upravene.length + zmeny.smazane.length;
  const neulozeno = pocetZmen > 0;
  useEffect(() => {
    if (!neulozeno) return undefined;
    const varuj = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", varuj);
    return () => window.removeEventListener("beforeunload", varuj);
  }, [neulozeno]);

  const komuText = (s) => {
    if (s.komu === "UZIVATEL") return tym.find((m) => m.id === s.user_id)?.name || "člen týmu";
    if (s.komu === "POZICE") return data.pozice[s.pozice] || "pozice";
    return data.komu[s.komu];
  };
  const pouzit = () => {
    const s = { ...edit, termin_dni: edit.termin_dni === "" ? 0 : edit.termin_dni, nazev: edit.nazev.trim() };
    if (s.komu !== "UZIVATEL") s.user_id = null;
    if (s.komu !== "POZICE") s.pozice = null;
    if (s.id) setKoncept((k) => k.map((x) => (x.id === s.id ? s : x)));
    else setKoncept((k) => [...k, { ...s, id: `novy-${++dalsiNove}` }]);
    setEdit(null); setUlozeno(false);
  };
  const prepnout = (s) => { setKoncept((k) => k.map((x) => (x.id === s.id ? { ...x, aktivni: !x.aktivni } : x))); setUlozeno(false); };
  const smazat = (s) => { setKoncept((k) => k.filter((x) => x.id !== s.id)); setUlozeno(false); };
  const zrusit = () => { setKoncept(data.sablony.map((s) => ({ ...s }))); setChyba(""); };
  const ulozit = async () => {
    setUkladam(true); setChyba("");
    let k = koncept;
    try {
      for (const s of zmeny.smazane) await api.delete(`/workflow/tasks/${s.id}`);
      for (const s of zmeny.upravene) await api.patch(`/workflow/tasks/${s.id}`, telo(s));
      for (const s of zmeny.nove) {
        const { data: nova } = await api.post("/workflow/tasks", telo(s));
        k = k.map((x) => (x === s ? { ...x, id: nova.id } : x));
      }
      prijmout((await api.get("/workflow/tasks")).data);
      setUlozeno(true); setTimeout(() => setUlozeno(false), 1800);
    } catch (e) {
      setChyba(formatApiError(e.response?.data?.detail));
      try { setData((await api.get("/workflow/tasks")).data); setKoncept(k); } catch { /* zůstane, jak je */ }
    } finally { setUkladam(false); }
  };
  const vychozi = async () => {
    if (!(await ask("Obnovit výchozí úkoly? Vaše úpravy šablon se ztratí, už založené úkoly zůstanou.", { ok: "Obnovit výchozí" }))) return;
    try { prijmout((await api.post("/workflow/tasks/defaults")).data); }
    catch (e) { setChyba(formatApiError(e.response?.data?.detail)); }
  };

  if (!data) {
    return <div className="bg-card border border-border rounded-[20px] p-6 text-sm text-muted-foreground">{chyba || "Načítám workflow…"}</div>;
  }

  const form = edit && (
    <div className="space-y-4" data-testid="workflow-edit">
      <div>
        <Label>Název úkolu</Label>
        <Input value={edit.nazev} onChange={(e) => setEdit({ ...edit, nazev: e.target.value })} className="h-11 mt-1" data-testid="wf-nazev" autoFocus={!mobile} />
        <p className="text-xs text-muted-foreground mt-1">Můžete použít {data.promenne.map((p) => `{${p}}`).join(", ")}.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><Label>Typ</Label>
          <Select value={edit.typ} onValueChange={(v) => setEdit({ ...edit, typ: v })}>
            <SelectTrigger className="h-11 mt-1" data-testid="wf-typ"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(data.typy).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label>Priorita</Label>
          <Select value={edit.priorita} onValueChange={(v) => setEdit({ ...edit, priorita: v })}>
            <SelectTrigger className="h-11 mt-1" data-testid="wf-priorita"><SelectValue /></SelectTrigger>
            <SelectContent>{data.priority.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
          </Select></div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><Label>Komu</Label>
          <Select value={edit.komu} onValueChange={(v) => setEdit({ ...edit, komu: v })}>
            <SelectTrigger className="h-11 mt-1" data-testid="wf-komu"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(data.komu).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
          </Select></div>
        {edit.komu === "POZICE" && (
          <div><Label>Pozice</Label>
            <Select value={edit.pozice || ""} onValueChange={(v) => setEdit({ ...edit, pozice: v })}>
              <SelectTrigger className="h-11 mt-1" data-testid="wf-pozice"><SelectValue placeholder="Vyberte…" /></SelectTrigger>
              <SelectContent>{Object.entries(data.pozice).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select></div>
        )}
        {edit.komu === "UZIVATEL" && (
          <div><Label>Člen týmu</Label>
            <Select value={edit.user_id || ""} onValueChange={(v) => setEdit({ ...edit, user_id: v })}>
              <SelectTrigger className="h-11 mt-1" data-testid="wf-user"><SelectValue placeholder="Vyberte…" /></SelectTrigger>
              <SelectContent>{tym.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
            </Select></div>
        )}
      </div>
      {edit.komu === "POZICE" && ["MANAGER", "WORKER", "DESIGNER"].includes(edit.pozice) && (
        <p className="text-xs text-muted-foreground -mt-2">Dostanou ho jen lidé s touto pozicí, kteří jsou na té zakázce.</p>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
        <div><Label>Termín: za kolik dnů</Label>
          <Input type="number" min="0" max="365" inputMode="numeric" value={edit.termin_dni} className="h-11 mt-1 tabular-nums" data-testid="wf-dni"
            onChange={(e) => setEdit({ ...edit, termin_dni: e.target.value === "" ? "" : Math.max(0, Math.min(365, Number(e.target.value))) })} /></div>
        <label className="flex items-center gap-2.5 h-11 text-sm font-medium cursor-pointer">
          <Checkbox checked={edit.pracovni_dny} onCheckedChange={(v) => setEdit({ ...edit, pracovni_dny: !!v })} data-testid="wf-pracovni" />
          pracovních dnů
        </label>
      </div>
      <div><Label>Popis (vždy stejný)</Label>
        <Textarea value={edit.popis} onChange={(e) => setEdit({ ...edit, popis: e.target.value })} className="mt-1 min-h-[80px]" data-testid="wf-popis" /></div>
      <div><Label>Odškrtne se sám</Label>
        <Select value={edit.uzavreni} onValueChange={(v) => setEdit({ ...edit, uzavreni: v })}>
          <SelectTrigger className="h-11 mt-1" data-testid="wf-uzavreni"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(data.uzavreni).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
        </Select></div>
      <div className="flex flex-col-reverse md:flex-row md:justify-end gap-2 pt-1">
        {edit.id && mobile && (
          <Button variant="ghost" onClick={() => { const s = koncept.find((x) => x.id === edit.id); setEdit(null); if (s) smazat(s); }}
            className="h-11 w-full rounded-full font-bold text-destructive" data-testid="wf-smazat"><Trash2 size={15} /> Smazat úkol</Button>
        )}
        <Button variant="outline" onClick={() => setEdit(null)} className="h-11 w-full md:w-auto rounded-full font-bold">Zrušit</Button>
        <Button onClick={pouzit} disabled={!edit.nazev.trim() || (edit.komu === "POZICE" && !edit.pozice) || (edit.komu === "UZIVATEL" && !edit.user_id)}
          className="h-11 w-full md:w-auto rounded-full font-bold px-6" data-testid="wf-pouzit">Použít</Button>
      </div>
    </div>
  );
  const editTitle = edit ? (edit.id ? "Upravit úkol" : "Nový úkol") : "";
  const editSub = edit ? `Když: ${data.faze[edit.faze]}` : "";

  return (
    <section className="bg-card border border-border rounded-[20px] p-5 md:p-6" data-testid="workflow-ukoly">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div className="min-w-0">
          <h2 className="text-[17px] font-bold">Workflow úkolů</h2>
          <p className="text-sm text-muted-foreground mt-0.5">Úkoly, které vzniknou samy, když zakázka vstoupí do fáze. U každé zakázky vznikne každý jen jednou.</p>
        </div>
        {canEdit && (
          <button type="button" onClick={vychozi} className="h-10 text-sm font-semibold text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5" data-testid="workflow-vychozi">
            <RotateCcw size={15} /> Obnovit výchozí
          </button>
        )}
      </div>
      {chyba && !neulozeno && <p className="text-sm text-destructive mb-3">{chyba}</p>}

      <div className="space-y-6">
        {skupiny.map((g) => (
          <div key={g.klic} data-testid={`workflow-faze-${g.klic}`}>
            <div className="flex items-center justify-between gap-3 mb-2">
              <h3 className="text-[12px] font-bold uppercase tracking-[0.08em] text-muted-foreground">Když: {g.nazev}</h3>
              {canEdit && (
                <button type="button" onClick={() => setEdit(PRAZDNA(g.klic))} data-testid={`workflow-pridat-${g.klic}`}
                  className="h-9 text-sm font-bold text-brand inline-flex items-center gap-1 hover:underline"><Plus size={15} /> Přidat úkol</button>
              )}
            </div>
            {g.sablony.length === 0 ? (
              <p className="text-sm text-muted-foreground border-b border-border pb-3">Žádné úkoly.</p>
            ) : (
              <div className="divide-y divide-border border border-border rounded-[14px]">
                {g.sablony.map((s) => {
                  const zmena = zmeny.nove.includes(s) || zmeny.upravene.includes(s);
                  return (
                    <div key={s.id} className="px-4 py-3 flex items-start gap-3" data-testid={`sablona-${s.id}`}>
                      <div className={`min-w-0 flex-1 transition-opacity ${s.aktivni ? "" : "opacity-50"} ${canEdit ? "cursor-pointer md:cursor-auto" : ""}`}
                        onClick={canEdit && mobile ? () => setEdit({ ...s }) : undefined}>
                        <div className="font-semibold flex items-center gap-2 min-w-0">
                          <span className="min-w-0 break-words">{s.nazev}</span>
                          {zmena && <span className="shrink-0 h-1.5 w-1.5 rounded-full bg-brand" title="Neuloženo" aria-label="Neuloženo" />}
                        </div>
                        <div className="text-[13px] text-muted-foreground flex flex-wrap gap-x-2.5 mt-0.5">
                          <span>{data.typy[s.typ]}</span>
                          <span>{komuText(s)}</span>
                          <span>{terminText(s)}</span>
                          {s.priorita !== "Střední" && <span>priorita {s.priorita.toLowerCase()}</span>}
                          {s.uzavreni !== "RUCNE" && <span>odškrtne se: {data.uzavreni[s.uzavreni].replace(/^Když /, "").toLowerCase()}</span>}
                        </div>
                      </div>
                      {canEdit && (
                        <div className="flex items-center gap-1 shrink-0">
                          <Switch checked={s.aktivni} onCheckedChange={() => prepnout(s)} aria-label="Zapnout úkol" data-testid={`sablona-aktivni-${s.id}`} />
                          <button type="button" onClick={() => setEdit({ ...s })} aria-label="Upravit" title="Upravit"
                            className="h-10 w-10 hidden md:inline-flex items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-secondary"><Pencil size={15} /></button>
                          <button type="button" onClick={() => smazat(s)} aria-label="Smazat" title="Smazat" data-testid={`sablona-smazat-${s.id}`}
                            className="h-10 w-10 hidden md:inline-flex items-center justify-center rounded-full text-muted-foreground hover:text-destructive hover:bg-destructive/10"><Trash2 size={15} /></button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>

      {canEdit && (neulozeno || ulozeno) && (
        <div className="sticky bottom-[calc(var(--bottom-nav-h,5rem)+env(safe-area-inset-bottom))] md:bottom-0 -mx-5 md:-mx-6 -mb-5 md:-mb-6 mt-5 px-5 md:px-6 py-3 bg-card/95 backdrop-blur border-t border-border rounded-b-[20px] flex flex-wrap items-center gap-3"
          data-testid="workflow-lista">
          <span className="text-sm text-muted-foreground flex items-center gap-2">
            {neulozeno ? <><span className="h-2 w-2 rounded-full bg-brand shrink-0" />Neuložené změny: {pocetZmen}</> : <><Check size={15} className="text-success" />Všechno uloženo.</>}
          </span>
          {chyba && <span className="text-sm text-destructive" data-testid="workflow-chyba">{chyba}</span>}
          <div className="flex gap-2 ml-auto">
            {neulozeno && <Button variant="outline" onClick={zrusit} disabled={ukladam} className="h-10 rounded-full font-bold" data-testid="workflow-zrusit">Zrušit</Button>}
            <Button onClick={ulozit} disabled={!neulozeno || ukladam} className="h-10 rounded-full font-bold px-6" data-testid="workflow-ulozit">{ukladam ? "Ukládám…" : "Uložit"}</Button>
          </div>
        </div>
      )}

      {mobile ? (
        <BottomSheet open={!!edit} onOpenChange={(o) => { if (!o) setEdit(null); }} title={editTitle} testid="workflow-sheet">
          {edit && <p className="text-sm text-muted-foreground -mt-1 mb-4">{editSub}</p>}
          {form}
        </BottomSheet>
      ) : (
        <Dialog open={!!edit} onOpenChange={(o) => { if (!o) setEdit(null); }}>
          <DialogContent className="max-w-[560px] rounded-[24px] p-0 gap-0 max-h-[92vh] overflow-y-auto">
            <DialogHeader className="px-6 sm:px-6 sm:pr-14 pt-5 pb-4 border-b border-border text-left">
              <DialogTitle className="text-xl font-extrabold">{editTitle}</DialogTitle>
              <DialogDescription>{editSub}</DialogDescription>
            </DialogHeader>
            <div className="p-6">{form}</div>
          </DialogContent>
        </Dialog>
      )}
    </section>
  );
}
